import React from 'react';
import styled from 'styled-components';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BenchmarkContainer = styled.div`
  margin: 30px 0;
`;

const BenchmarkTitle = styled.h2`
  font-size: 20px;
  color: #333;
  margin: 30px 0 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
`;

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const ChartCard = styled.div`
  background-color: white;
  border-radius: 0;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 300px;
`;

const ContentTitle = styled.h3`
  font-size: 16px;
  color: #333;
  margin-bottom: 10px;
`;

const PerformanceIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  padding: 8px 16px;
  border-radius: 0;
  font-weight: 600;
  font-size: 14px;
  background-color: ${props => props.performance === 'excellent' ? '#dcfce7' : 
                                props.performance === 'good' ? '#fef3c7' : '#fee2e2'};
  color: ${props => props.performance === 'excellent' ? '#166534' : 
                   props.performance === 'good' ? '#92400e' : '#dc2626'};
`;

// Benchmarks moyens du marché - Campagnes d'acquisition
const BENCHMARKS = {
  sms: {
    deliveryRate: 88,
    previewRate: 55,
    clickRate: 1.8
  },
  email: {
    deliveryRate: 75,
    openRate: 12,
    clickToOpenRate: 6.2
  }
};

function BenchmarkComparison({ smsData, emailData, smsDeliveryRate, smsPreviewRate, smsClickToPreviewRate, emailDeliveryRate, emailOpenRate, emailClickToOpenRate }) {
  
  const getPerformanceLevel = (actual, benchmark) => {
    const ratio = actual / benchmark;
    if (ratio >= 1.2) return 'excellent';
    if (ratio >= 0.8) return 'good';
    return 'needs-improvement';
  };

  const getPerformanceText = (level) => {
    switch(level) {
      case 'excellent': return '🎉 Excellent - Au-dessus de la moyenne';
      case 'good': return '✅ Bon - Dans la moyenne';
      default: return '📈 À améliorer';
    }
  };

  const smsComparisonData = [
    {
      name: 'Délivrance',
      'Votre campagne': parseFloat(smsDeliveryRate),
      'Moyenne marché': BENCHMARKS.sms.deliveryRate
    },
    {
      name: 'Preview',
      'Votre campagne': parseFloat(smsPreviewRate),
      'Moyenne marché': BENCHMARKS.sms.previewRate
    },
    {
      name: 'Clic/Preview',
      'Votre campagne': parseFloat(smsClickToPreviewRate),
      'Moyenne marché': BENCHMARKS.sms.clickRate
    }
  ];

  const emailComparisonData = [
    {
      name: 'Délivrance',
      'Votre campagne': parseFloat(emailDeliveryRate),
      'Moyenne marché': BENCHMARKS.email.deliveryRate
    },
    {
      name: 'Ouverture',
      'Votre campagne': parseFloat(emailOpenRate),
      'Moyenne marché': BENCHMARKS.email.openRate
    },
    {
      name: 'Clic/Ouverture',
      'Votre campagne': parseFloat(emailClickToOpenRate),
      'Moyenne marché': BENCHMARKS.email.clickToOpenRate
    }
  ];

  return (
    <BenchmarkContainer>
      <BenchmarkTitle>📊 Comparaison avec les Moyennes du Marché</BenchmarkTitle>
      
      <ChartsContainer>
        {smsData && (
          <ChartCard>
            <ContentTitle>Performance SMS vs Marché</ContentTitle>
            <ResponsiveContainer width="100%" height="80%">
              <BarChart data={smsComparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} />
                <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                <Legend />
                <Bar dataKey="Votre campagne" fill="#6ed216" />
                <Bar dataKey="Moyenne marché" fill="#94a3b8" />
              </BarChart>
            </ResponsiveContainer>
            <PerformanceIndicator performance={getPerformanceLevel(parseFloat(smsClickToPreviewRate), BENCHMARKS.sms.clickRate)}>
              {getPerformanceText(getPerformanceLevel(parseFloat(smsClickToPreviewRate), BENCHMARKS.sms.clickRate))}
            </PerformanceIndicator>
          </ChartCard>
        )}

        {emailData && (
          <ChartCard>
            <ContentTitle>Performance Email vs Marché</ContentTitle>
            <ResponsiveContainer width="100%" height="80%">
              <BarChart data={emailComparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} />
                <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                <Legend />
                <Bar dataKey="Votre campagne" fill="#6ed216" />
                <Bar dataKey="Moyenne marché" fill="#94a3b8" />
              </BarChart>
            </ResponsiveContainer>
            <PerformanceIndicator performance={getPerformanceLevel(parseFloat(emailClickToOpenRate), BENCHMARKS.email.clickToOpenRate)}>
              {getPerformanceText(getPerformanceLevel(parseFloat(emailClickToOpenRate), BENCHMARKS.email.clickToOpenRate))}
            </PerformanceIndicator>
          </ChartCard>
        )}
      </ChartsContainer>
    </BenchmarkContainer>
  );
}

export default BenchmarkComparison;